/**
 * Utility module for URL-related functions
 *
 */

/**
 * Get the current domain of the shop
 *
 * @returns {string} The current domain of the shop
 */
const getCurrentDomain = () => {
  // Get current shop url
  const url = window.location.hostname;

  // If Dev IP address, return the hostname, ie: 127.0.0.1
  if (!Number.isNaN(url.substring(0, 3))) {
    return url;
  }

  // Extracts domain only from current shop (i.e. removes 'www.' and leaves '.allbirds.com')
  // This step is necessary because there are some domains with TLDs that are multi-part (co.uk)
  let domain = url.substring(url.indexOf('.'));

  // if there's no subdomain in the current window location, prepend the domain with
  // a period to include any subdomains that are navigated to
  if (domain.charAt(0) !== '.') {
    domain = `.${domain}`;
  }

  return domain;
};

/**
 * Parse the query string into an object
 *
 * @param {string} queryString - The query string to parse
 * @returns {Object} The parsed query string as an object
 */
const parseQueryString = (queryString = window.location.search) => {
  const params = {};
  const searchParams = new URLSearchParams(queryString);

  for (const [key, value] of searchParams) {
    params[key] = value;
  }

  return params;
};

/**
 * Update the browser history with new query params
 *
 * @param {Object} options - The options object
 * @param {string} [options.pathname=window.location.pathname] - The pathname to use
 * @param {Object} options.searchParams - The object containing the new query params to add/update
 * @returns {void}
 */
const updateHistory = ({ pathname = window.location.pathname, searchParams = {} }) => {
  // Get the current search parameters
  const currentSearchParams = new URLSearchParams(window.location.search);

  // Set or update parameters from the input object
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === null || value === undefined) {
      currentSearchParams.delete(key);
    } else {
      currentSearchParams.set(key, String(value));
    }
  }

  // Construct the new URL
  const newSearchString = currentSearchParams.toString();
  const newUrl = newSearchString ? `${pathname}?${newSearchString}` : pathname;

  // Update the browser history without reloading the page
  window.history.replaceState({}, '', newUrl);
};

/**
 * Remove given query param list from the current URL
 *
 * @param {Array} queryParamsToRemoveList - List of query params to remove
 * @returns {void}
 */
const removeQueryString = (queryParamsToRemoveList = []) => {
  const searchParams = {};

  queryParamsToRemoveList.forEach((queryParamToRemove) => {
    searchParams[queryParamToRemove] = null;
  });

  updateHistory({ searchParams });
};

export { getCurrentDomain, parseQueryString, removeQueryString, updateHistory };
